// @ts-strict-ignore
import { Euler, Material, Mesh, Vector3 } from 'three';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { CloneUtils } from '@shared/util/clone-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ConnectionCreator } from '@article/components/connections/connection-creation/connection-creator';
import { ConnectionDefinition } from '@article/components/connections/connection-creation/connection-configuration';
import { ConnectionConverter } from '@article/components/connections/connection-creation/connection-converter';
import { MeshUtils } from '../../common/util/mesh-utils';
import { Object3DUtils } from '../../common/util/object-3d-utils';
import { Injectable } from '@angular/core';
import { ConnectionFactory } from '../connection.factory';
import { ConnectionInfoUtils } from './connection-info-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';

@Injectable({ providedIn: 'root' })
export class ConnectionUtils {
  constructor(
    private _connectionFactory: ConnectionFactory,
    private _connectionCreator: ConnectionCreator
  ) {
  }

  public createTargetBySourceConnection(targetContainer: ArticleContainer, sourceConnection: Connection): Connection {
    const connectionMesh: Mesh = MeshUtils.cloneMesh(sourceConnection.mesh);
    Object3DUtils.disposeAllChildren(connectionMesh);
    connectionMesh.parent = null;
    connectionMesh.material = (sourceConnection.mesh.material as Material).clone();
    connectionMesh.position.copy(new Vector3());
    connectionMesh.rotation.copy(new Euler());

    const connectionMetaElement: MetaElementVO = CloneUtils.cloneMetaElementVO(sourceConnection.metaElementVO);
    MetaElementUtils.addMetaElementToArticle(targetContainer.article, connectionMetaElement);

    const connection: Connection = this._connectionFactory.getNewInstance(targetContainer, connectionMesh, sourceConnection.type);
    const sourceMeshWidthOrRadius: number = sourceConnection.meshIsCircle ? sourceConnection.meshRadius : sourceConnection.meshWidth;
    const sourceMeshLength: number = sourceConnection.meshIsCircle ? null : sourceConnection.meshLength;

    targetContainer.setConnection(connection);
    targetContainer.model.add(connectionMesh);
    connection.updateMesh(sourceConnection.meshPosition, sourceConnection.meshRotationDegree, sourceMeshWidthOrRadius, sourceMeshLength);

    return connection;
  }

  public updateInternalConnectionPropertiesByName(connectionName: string, article: ArticleVO): void {
    const metaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, connectionName);
    if (!metaElement) {
      return;
    }

    const linkedMetaElements: MetaElementVO[] = this._connectionCreator.getMultipurposeAreasByConnectionName(connectionName, article);
    for (const linkedMetaElement of linkedMetaElements) {
      if (linkedMetaElement.name !== metaElement.name) {
        linkedMetaElement.behaviorProperties = ConnectionInfoUtils.cloneProperties(metaElement.behaviorProperties);
      }
    }
  }

  public updateInternalConnectionProperties(metaElement: MetaElementVO, article: ArticleVO): void {
    if (!metaElement) {
      return;
    }

    this.updateInternalConnectionPropertiesByName(metaElement.name, article);
  }

  private createTargetLinkedConnections(targetContainer: ArticleContainer, sourceConnections: Connection[]): void {
    const createdMetaElements: MetaElementVO[] = [];
    sourceConnections.forEach((c: Connection) => {
      const createdConnection: Connection = this.createTargetBySourceConnection(targetContainer, c);
      const createdMetaElement: MetaElementVO = createdConnection.metaElementVO;
      MetaElementUtils.removeSetIdFromMultipurposeArea(createdMetaElement);
      createdMetaElements.push(createdMetaElement);
    });
    MetaElementUtils.applySetIdToMultipurposeAreasAsArray(createdMetaElements);
  }

  public replaceTargetBySourceConnection(
    connectionConverter: ConnectionConverter,
    sourceConnectionName: string,
    sourceConnections: Connection[],
    targetConnectionName: string,
    targetContainer: ArticleContainer,
    useSourceConnectionNameForConversion: boolean
  ): void {
    const sourceConnection: Connection = ConnectionInfoUtils.findMainConnectionIgnoringPostfix(sourceConnections, sourceConnectionName);
    if (!sourceConnection) {
      return;
    }

    const sourceLinkedConnections: Connection[] = ConnectionInfoUtils.findLinkedConnectionsInSource(sourceConnection, sourceConnections);
    const targetConnection: Connection = ConnectionInfoUtils.findMainConnectionIgnoringPostfix(targetContainer.connections, targetConnectionName);
    if (targetConnection) {
      let targetLinkedConnections: Connection[];
      if (
        MetaElementUtils.isMultipurposeArea(sourceConnection.metaElementVO) &&
        MetaElementUtils.isMultipurposeArea(targetConnection.metaElementVO)
      ) {
        targetLinkedConnections = this._connectionCreator.getMultipurposeConnectionsBySelection(targetConnection);
      } else {
        const definition: ConnectionDefinition = this._connectionCreator.getConnectionDefinitionByMetaElement(sourceConnection.metaElementVO, true);
        if (!useSourceConnectionNameForConversion) {
          definition.name = null;
        }
        targetLinkedConnections = connectionConverter.convertConnection(targetConnection, definition);
      }

      targetLinkedConnections.forEach((targetConnection: Connection) => {
        const isInternal: boolean = targetConnection.metaElementVO.isInternal;
        ConnectionInfoUtils.modifyTargetBySourceConnection(
          targetConnection,
          sourceLinkedConnections.find((connection: Connection) => connection.metaElementVO.isInternal === isInternal)
        );
      });
    } else {
      this.createTargetLinkedConnections(targetContainer, sourceLinkedConnections);
    }
  }

  public static isDirectPrintingTextCheckConnection(connection: Connection): boolean {
    return !ArticleUtils.isLabelingArticle(connection.article)
      && PropertyUtils.containsProperty(connection.metaElementVO.behaviorProperties, MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS);
  }
}
